// AdminDashboard.js
import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import AdminUserManagement from './Admin/AdminUserManagement';
import { 
  UsersIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

const AdminDashboard = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');

  const tabs = [
    { id: 'users', name: '사용자 관리', icon: UsersIcon },
    { id: 'stats', name: '통계', icon: ChartBarIcon },
    { id: 'reports', name: '리포트', icon: DocumentTextIcon },
    { id: 'settings', name: '설정', icon: Cog6ToothIcon }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-purple-50">
      <div className="max-w-7xl mx-auto p-6">
        {/* 헤더 */}
        <div className="bg-white rounded-lg shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                🛡️ 관리자 대시보드
              </h1>
              <p className="text-gray-600 mt-2">
                안녕하세요, {user.username} 관리자님! SafeAI Kids 플랫폼을 관리하세요.
              </p>
            </div>
            <div className="flex items-center justify-center h-16 w-20 bg-gradient-to-br from-purple-500 to-blue-600 rounded-2xl shadow-lg">
              <span className="text-white text-sm font-bold">SafeAI</span>
            </div>
          </div>

          {/* 탭 네비게이션 */}
          <div className="flex flex-wrap space-x-2 sm:space-x-4 border-b border-gray-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 py-3 px-4 sm:px-6 font-medium border-b-2 transition-colors text-sm sm:text-base ${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                >
                  <Icon className="h-5 w-5" />
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden">{tab.name.split(' ')[0]}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* 탭 콘텐츠 */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          {activeTab === 'users' && <AdminUserManagement />}
          
          {activeTab === 'stats' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">📊 시스템 통계</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-blue-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <UsersIcon className="h-8 w-8 text-blue-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-blue-600">전체 사용자</p>
                      <p className="text-2xl font-semibold text-gray-900">-</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-green-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <ChartBarIcon className="h-8 w-8 text-green-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-green-600">활성 세션</p>
                      <p className="text-2xl font-semibold text-gray-900">-</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-yellow-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <DocumentTextIcon className="h-8 w-8 text-yellow-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-yellow-600">일일 메시지</p>
                      <p className="text-2xl font-semibold text-gray-900">-</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-purple-50 p-6 rounded-lg">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <Cog6ToothIcon className="h-8 w-8 text-purple-600" />
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-purple-600">시스템 상태</p>
                      <p className="text-2xl font-semibold text-gray-900">정상</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 text-center text-gray-500">
                <p>📈 상세한 통계 기능은 개발 중입니다.</p>
              </div>
            </div>
          )}
          
          {activeTab === 'reports' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">📋 리포트</h2>
              <div className="text-center text-gray-500 py-12">
                <DocumentTextIcon className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                <p className="text-lg">리포트 기능은 개발 중입니다.</p>
                <p className="text-sm mt-2">사용자 활동, 안전성 점검 등의 리포트를 제공할 예정입니다.</p>
              </div>
            </div>
          )}
          
          {activeTab === 'settings' && (
            <div className="p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">⚙️ 시스템 설정</h2>
              <div className="space-y-6">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h3 className="text-lg font-medium text-gray-900 mb-2">일반 설정</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">사용자 등록 허용</span>
                      <div className="w-12 h-6 bg-gray-300 rounded-full relative cursor-pointer">
                        <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-transform"></div>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">교사 자동 승인</span>
                      <div className="w-12 h-6 bg-gray-300 rounded-full relative cursor-pointer">
                        <div className="w-5 h-5 bg-white rounded-full absolute top-0.5 left-0.5 transition-transform"></div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="text-center text-gray-500">
                  <p>🔧 고급 설정 기능은 개발 중입니다.</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 하단 링크 */}
        <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
          <div className="border-t border-gray-200 pt-6">
            <div className="flex flex-wrap justify-center space-x-8 text-sm text-gray-600">
              <a 
                href="/privacy-policy" 
                className="hover:text-blue-600 transition-colors"
              >
                📋 개인정보 보호정책
              </a>
              <a 
                href="/consent" 
                className="hover:text-blue-600 transition-colors"
              >
                👨‍👩‍👧‍👦 학부모 동의서
              </a>
              <span className="text-gray-400">|</span>
              <span className="font-medium text-blue-600">
                🛡️ SafeAI Kids - 안전한 AI 교육 플랫폼
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;